/**
 * AvailabilityTimesForm — 022-checkin-checkout-times
 *
 * Inline form rendered inside the availability list row (not a modal) that lets
 * the operator configure default check-in/out times and auto-flag settings for
 * an individual availability period.
 *
 * Design decisions:
 * - Uses native <input type="time"> (no new deps).
 * - All times are UTC wall-clock values; a "(UTC)" label is shown next to every
 *   time field per the feature spec.
 * - "" → null conversion handled here: an empty <input type="time"> is treated
 *   as "clear the time field" (sends null to the API).
 * - Shows an informational warning when the availability has an active booking
 *   (status === 'BOOKED') because the backend will NOT retroactively apply the
 *   change to that booking.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, AlertTriangle } from 'lucide-react';
import { Availability } from '../types/availability';

interface AvailabilityTimesFormProps {
  availability: Availability;
  onSubmit: (data: {
    checkInTime: string | null;
    checkOutTime: string | null;
    autoCheckIn: boolean;
    autoCheckOut: boolean;
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export const AvailabilityTimesForm = ({
  availability,
  onSubmit,
  onCancel,
  isLoading,
}: AvailabilityTimesFormProps) => {
  const { t } = useTranslation(['forms', 'pages', 'common']);

  const [checkInValue, setCheckInValue] = useState<string>(
    availability.checkInTime ?? ''
  );
  const [checkOutValue, setCheckOutValue] = useState<string>(
    availability.checkOutTime ?? ''
  );
  const [autoCheckIn, setAutoCheckIn] = useState<boolean>(
    availability.autoCheckIn ?? false
  );
  const [autoCheckOut, setAutoCheckOut] = useState<boolean>(
    availability.autoCheckOut ?? false
  );

  const hasActiveBooking = availability.status === 'BOOKED';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      // "" → null (clears the field); otherwise pass the "HH:mm" string as-is
      checkInTime: checkInValue || null,
      checkOutTime: checkOutValue || null,
      autoCheckIn,
      autoCheckOut,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-4 pt-4 border-t border-gray-200 space-y-4"
      aria-label={t('forms:checkInTime') + ' / ' + t('forms:checkOutTime')}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <Clock size={16} className="text-primary-500" />
        <span>
          {t('forms:checkInTime')} / {t('forms:checkOutTime')}
        </span>
        <span className="text-xs text-gray-500 font-normal ml-1">
          {t('forms:timesUtcNote')}
        </span>
      </div>

      {hasActiveBooking && (
        <div
          role="alert"
          className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" />
          <span>{t('pages:activeBookingWarning')}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Check-in time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('forms:checkInTime')}
            <span className="ml-1 text-xs text-gray-500 font-normal">
              {t('pages:utcLabel')}
            </span>
          </label>
          <input
            type="time"
            value={checkInValue}
            onChange={(e) => setCheckInValue(e.target.value)}
            className="input"
            aria-label={t('forms:checkInTime')}
          />
        </div>

        {/* Check-out time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('forms:checkOutTime')}
            <span className="ml-1 text-xs text-gray-500 font-normal">
              {t('pages:utcLabel')}
            </span>
          </label>
          <input
            type="time"
            value={checkOutValue}
            onChange={(e) => setCheckOutValue(e.target.value)}
            className="input"
            aria-label={t('forms:checkOutTime')}
          />
        </div>
      </div>

      {/* Auto flags */}
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={autoCheckIn}
            onChange={(e) => setAutoCheckIn(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          {t('forms:autoCheckIn')}
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={autoCheckOut}
            onChange={(e) => setAutoCheckOut(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          {t('forms:autoCheckOut')}
        </label>
      </div>

      <div className="text-xs text-gray-500">
        {t('forms:multiAvailabilityTimesNote')}
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
        >
          {t('common:cancel')}
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary text-sm"
        >
          {isLoading ? t('common:saving') : t('common:save')}
        </button>
      </div>
    </form>
  );
};
